import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import TourContainer from './components/TourContainer/TourContainer';
import TourSelection from './mainApp/pages/TourSelection/TourSelection';
import TourInformation from './mainApp/pages/TourInformation/TourInformation';
import './App.css';
import TourDaySelection from 'mainApp/pages/TourDaySelection/TourDaySelection';
import { PorscheDesignSystemProvider } from '@porsche-design-system/components-react';
import CancelScreen from 'mainApp/pages/StripePayment/Cancel';
import SuccessScreen from 'mainApp/pages/StripePayment/Success';
import NotFound from 'mainApp/pages/404/NotFound';
import useGetUserAgent from 'mainApp/assets/utils/hooks/useGetUserAgent';
import { LanguageProvider } from 'mainApp/assets/utils/context/LanguageContext';
import { PVariablesCustomHydrator } from 'mainApp/assets/visual/styles/globalStyles/globalJsxStyles';
import useIsPhone from 'mainApp/assets/utils/hooks/useIsPhone';
import AppLandScapeEnforcer from 'components/AppLandScapeEnforcer/AppLandScapeEnforcer';
import { IsRotatedProvider } from 'mainApp/assets/utils/context/isRotatedContext';

const pageVariants = {
    out_FadeOut: {
        opacity: 0,
        transition: {
            duration: 0.5,
            delay: 0.3
        }
    },
    initial: {
        opacity: 0,
        x: '100vw'
    },
    in: {
        opacity: 1,
        x: 0,
        transition: {
            duration: 0.5,
            delay: 0.3
        }
    },
    out: {
        opacity: 0,
        x: '-100vw',
        transition: {
            duration: 0.5,
            delay: 0.3
        }
    },

    slideInFromRight: {
        opacity: 0,
        x: '100vw',
        transition: {
            duration: 0.5,
            delay: 0.3
        }
    },
    slideOutToLeft: {
        opacity: 0,
        x: '-100vw',
        transition: {
            duration: 0.5,
            delay: 0.3
        }
    },
    slideOutToRight: {
        opacity: 0,
        x: '100vw',
        transition: {
            duration: 0.5,
            delay: 0.3
        }
    },

    generalOut: {
        opacity: 0,
        transition: {
            duration: 0.6,
            // delay: 0.3
        }
    },
    generalInitial: {
        opacity: 1
    },
};

const pageTransition = {
    type: 'tween',
    duration: 0.5
};

const MotionRoute = ({ element, initialVariants, exitVariants }) => (
    <motion.div
        initial={initialVariants ? initialVariants : "initial"}
        animate="in"
        exit={exitVariants ? exitVariants : "out"}
        variants={pageVariants}
        transition={pageTransition}
        className="motionContainerStyle"

    >
        {element}
    </motion.div>
);

const App = () => {
    const location = useLocation();
    const [prevLocation, setPrevLocation] = useState(location);
    const [direction, setDirection] = useState('forward');
    const isPhone = useIsPhone();

    useEffect(() => {
        if (location.pathname.includes('/tour/')) {
            if (prevLocation.pathname.includes('/tour/') && location.pathname !== prevLocation.pathname) {
                setDirection('forward');
            } else if (location.pathname === '/') {
                setDirection('backward');
            } else {
                setDirection('forward');
            }
        } else if (prevLocation.pathname.includes('/tour/') && location.pathname !== '/') {
            setDirection('forward');
        }
        setPrevLocation(location);
    }, [location, prevLocation]);

    const getVariants = () => {
        if (location.pathname.includes('/tour/')) {
            if (direction === 'forward') {
                return { initial: 'slideInFromRight', exit: 'slideOutToLeft' };
            } else if (direction === 'backward') {
                return { initial: 'slideInFromRight', exit: 'slideOutToRight' };
            }
        }
        return { initial: 'initial', exit: 'out' };
    };

    const getCurrentScreen = () => {
        if (location.pathname === '/') {
            return 'TourSelection';
        } else if (location.pathname.includes('/tour/')) {
            return 'TourInformation';
        }
        return 'Other';
    };

    return (
        <TourContainer screen={getCurrentScreen()}>
            <AnimatePresence mode="wait">
                <Routes location={location} key={location.pathname}>
                    <Route
                        path="/"
                        element={<MotionRoute
                            element={<TourSelection />}
                            initialVariants="generalOut"
                            exitVariants="generalOut"
                        />}
                    />
                    <Route
                        path="/tour/details/:title"
                        element={<MotionRoute
                            element={<TourInformation isPhone={isPhone} />}
                            initialVariants="generalOut"
                            // initialVariants={getVariants().initial}
                            exitVariants="generalOut"
                        // exitVariants={getVariants().exit}
                        />}
                    />
                    <Route
                        path="/reservation/:title"
                        element={<MotionRoute
                            element={<TourDaySelection />}
                            initialVariants="generalOut"
                            // initialVariants={getVariants().initial}
                            exitVariants="generalOut"
                        // exitVariants={getVariants().exit}
                        />}
                    />
                    <Route
                        path="/reservation/cancel"
                        element={<MotionRoute
                            element={<CancelScreen />}
                            initialVariants={getVariants().initial}
                            exitVariants={getVariants().exit}
                        />}
                    />
                    <Route
                        path="/reservation/success"
                        element={<MotionRoute
                            element={<SuccessScreen />}
                            initialVariants={getVariants().initial}
                            exitVariants={getVariants().exit}
                        />}
                    />
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </AnimatePresence>

        </TourContainer>
    );
};

const AppWrapper = () => (
    <IsRotatedProvider>
        <AppLandScapeEnforcer>
            <Router>
                <PorscheDesignSystemProvider>
                    <LanguageProvider>
                        <App />
                    </LanguageProvider>
                </PorscheDesignSystemProvider>
            </Router>
        </AppLandScapeEnforcer>
    </IsRotatedProvider>
);

export default AppWrapper;
